import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { bugsnag } from 'lib/external/bugsnag';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { getWooProducedParts } from 'lib/selectors/getWooProducedParts';
import { getMaterialPickingEnabled } from 'lib/selectors/getMaterialPickingEnabled';
import usePickingSummary from 'lib/hooks/usePickingSummary';

/**
 * Builds the `materialCoverageByWoo` payload the embedded labor ticket form uses to
 * block submitting more parts than there is picked material to cover.
 *
 * The payload is keyed by the current WOO's `workOrderOperationRef` so the embed can
 * match it against `ticket.workOrderOperationRef`. For each required material,
 * `available` is picked quantity minus the material already consumed by previously
 * submitted labor tickets (good + bad — scrap consumes material). The baseline counts
 * every ticket on the WOO, including the one open in the form; the embed subtracts the
 * parts that ticket already carries from the entered quantity, so only the increase is
 * checked against `available` (mm-integrations labor-ticket-form getCoverageDemand).
 *
 * Once any material on the WOO has been picked, every required material is sent,
 * including ones with nothing picked yet: an operator may start a job after picking
 * some of its materials, but cannot report parts until the rest are picked. When
 * nothing has been picked (e.g. the job was already running when picking turned on)
 * there is no coverage to enforce, and sending it would block the operator from
 * submitting labor for parts produced before picking began.
 *
 * Returns { materialCoverageByWoo, ready }. materialCoverageByWoo is null when there
 * is no current WOO, when the WOO has no material requirements (feature off for
 * that job), or when nothing has been picked. `ready` is false until the WOO's
 * material data has loaded once, so the form is not opened without its check. A
 * failed read counts as ready: it is reported, and the form opens without the check
 * rather than blocking labor.
 *
 * @returns {{ materialCoverageByWoo: (Object|null), ready: boolean }}
 */
const useMaterialCoverageContext = () => {
  const materialPickingEnabled = useSelector(getMaterialPickingEnabled);
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const submittedParts = useSelector(getWooProducedParts);

  const wooRef = currentWorkOrderOp?.workOrderOperationRef;
  const { materials, loaded, error } = usePickingSummary(wooRef);

  useEffect(() => {
    if (materialPickingEnabled && error) {
      bugsnag.notify(error, (event) => {
        event.addMetadata('materialCoverage', {
          workOrderOperationRef: wooRef,
        });
      });
    }
  }, [materialPickingEnabled, error, wooRef]);

  const ready = !materialPickingEnabled || !wooRef || loaded || Boolean(error);

  const materialCoverageByWoo = useMemo(() => {
    if (!materialPickingEnabled || !wooRef || !materials.length) {
      return null;
    }

    const anyPicked = materials.some((mat) => {
      return mat.pickedQty > 0;
    });

    if (!anyPicked) {
      return null;
    }

    const coverageMaterials = materials.map((mat) => {
      const qtyPerUnit = mat.qtyPerUnit || 0;
      return {
        itemNumber: mat.itemNumber,
        // The embed renders `description` in its blocking message; itemNumber is
        // the display name until the connector supplies a real description.
        description: mat.itemNumber,
        qtyPerUnit,
        // Unit of measure from availableMaterials (e.g. "lb"), for display.
        unit: mat.unit || null,
        // Raw picked quantity, for display. Unlike `available` this never goes
        // negative, so the embed can show what the operator actually picked.
        pickedQty: mat.pickedQty,
        available: mat.pickedQty - qtyPerUnit * submittedParts,
      };
    });

    return {
      [wooRef]: { materials: coverageMaterials },
    };
  }, [materialPickingEnabled, materials, submittedParts, wooRef]);

  return { materialCoverageByWoo, ready };
};

export default useMaterialCoverageContext;
